import { useQuery } from '@tanstack/react-query';
import { GET_PATIENT_PROFILES } from '../../contracts';
import { getPatientProfileById } from '../api/patients';

export interface UseQueryPatientProfileByIdOptions {
  /**
   * Gate for the fetch itself (AND-ed with `isAuthenticated` and the id
   * presence). Defaults to true.
   */
  enabled?: boolean;
  /**
   * `!!state.auth.token && !!userData`, composed app-side (cohort-C): the
   * package cannot reach the Rematch store or the workspace user record.
   */
  isAuthenticated?: boolean;
}

/**
 * Single-patient read (`GET /patient-profiles/{id}`), the full-screen page's
 * direct-URL fallback under server pagination: the roster query only covers
 * the clinician's own patients, while paginated POST /v2.1 includes
 * workspace-level ones.
 *
 * Key: `[GET_PATIENT_PROFILES, { patientProfileId }]` — shares the roster's
 * family so existing mutations invalidate it by prefix, and the object second
 * element avoids colliding with the roster (`null`) and session-scoped
 * (sessionId) keys.
 *
 * Returns the profile as the backend serves it. The endpoint does not include
 * `session_count` / `last_session_created_at` until the COLAB-406 enrichment
 * lands — callers satisfy the `PatientProfileWithSessionCount` contract with
 * the same cast the full-screen page uses.
 */
export function useQueryPatientProfileById(
  patientProfileId: string | undefined,
  { enabled = true, isAuthenticated = false }: UseQueryPatientProfileByIdOptions = {}
) {
  return useQuery({
    queryKey: [GET_PATIENT_PROFILES, { patientProfileId }],
    enabled: Boolean(enabled && isAuthenticated && !!patientProfileId),
    queryFn: async () => {
      const { data } = await getPatientProfileById(patientProfileId as string);
      return data.data;
    },
  });
}
